/**
 * La columna d'habitacions de la graella (F7 · en Pere 29-SET): 72 px fixos i «H101 PB» en una
 * línia. Amplada i abreviatura de la planta, sense React (amb tests).
 */

/**
 * Amplada (px) de la columna d'habitacions: la cel·la de cada fila, la cantonada de la capçalera i
 * la columna del separador de propietat (vista «Totes»). Fixa (`width` i `minWidth`), perquè cada
 * fila casi amb la capçalera.
 */
export const ROOM_COLUMN_WIDTH = 72;

/** Ordinals de planta (català i castellà) → número. */
const FLOOR_ORDINALS: Record<string, number> = {
  primera: 1,
  primer: 1,
  segona: 2,
  segon: 2,
  segunda: 2,
  segundo: 2,
  tercera: 3,
  tercer: 3,
  tercero: 3,
  quarta: 4,
  quart: 4,
  cuarta: 4,
  cuarto: 4,
  cinquena: 5,
  cinquè: 5,
  quinta: 5,
  quinto: 5,
  sisena: 6,
  sisè: 6,
  sexta: 6,
  sexto: 6,
  setena: 7,
  setè: 7,
  séptima: 7,
  vuitena: 8,
  vuitè: 8,
  octava: 8,
  novena: 9,
  novè: 9,
  desena: 10,
  desè: 10,
  décima: 10,
};

/** Planta baixa / baja, i el número d'una planta escrita amb ordinal («Segona planta») o xifra
 * («1a planta», «Planta 1 - …»). `null` si el text no diu quina planta és. */
function floorNumber(text: string): number | 'ground' | null {
  const t = text.trim().toLowerCase();
  if (/^planta\s+(?:baix[ae]|baj[ao])\b/.test(t) || /^(?:baixos?|bajos?)\b/.test(t)) return 'ground';
  // «Segona planta», «planta segona»
  const ordinal =
    /^([\p{L}]+)\s+planta\b/u.exec(t)?.[1] ?? /^planta\s+([\p{L}]+)\b/u.exec(t)?.[1];
  if (ordinal && FLOOR_ORDINALS[ordinal] != null) return FLOOR_ORDINALS[ordinal];
  // «1a planta», «3ª planta», «2n pis», «Planta 1 - Primera puerta derecha»
  const digits = /^(\d+)\s*(?:a|ª|º|o|r|n|t|è|e)?\.?\s+(?:planta|pis|piso)\b/.exec(t)?.[1] ??
    /^(?:planta|pis|piso)\s+(\d+)\b/.exec(t)?.[1];
  if (digits != null) {
    const n = Number(digits);
    return n === 0 ? 'ground' : n;
  }
  return null;
}

/**
 * Abreviatura de la planta per a la columna d'habitacions: «Planta baixa» → `PB`, «Primera planta»
 * → `1P`, «Segona planta» → `2P` (i qualsevol «N-èsima planta» → `NP`); sense planta → `''`;
 * qualsevol altre text → les inicials en majúscula (màxim 3).
 */
export function floorAbbreviation(location: string | null | undefined): string {
  const text = (location ?? '').trim();
  if (!text) return '';
  const floor = floorNumber(text);
  if (floor === 'ground') return 'PB';
  if (floor != null) return `${floor}P`;
  return text
    .split(/[\s\-–—_/.,·]+/)
    .map((word) => word.match(/[\p{L}\p{N}]/u)?.[0] ?? '')
    .filter(Boolean)
    .slice(0, 3)
    .join('')
    .toUpperCase();
}

/** `title` de la cel·la d'habitació: «H101 · Planta baixa» (o només el nom si no hi ha planta). */
export function roomCellTitle(room: { displayName: string; physicalLocation?: string | null }): string {
  const location = room.physicalLocation?.trim();
  return location ? `${room.displayName} · ${location}` : room.displayName;
}
